import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { api } from "../api.js";
import { Icon } from "./Icon.js";
import { setTheme, useTheme, type Theme } from "../theme.js";
import { SystemLoad } from "./SystemLoad.js";
import { useQueueCount } from "../pages/Queue.js";

/** Light / Dark / System. The same control sits in Settings. */
export const ThemeSwitch: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const t = useTheme();
  const opts: [Theme, string][] = [["light", "Light"], ["dark", "Dark"], ["system", "System"]];
  return (
    <div className="tseg" role="group" aria-label="Appearance" title="Light, dark, or follow the Mac">
      {opts.map(([v, l]) => <button key={v} className={t === v ? "on" : ""} onClick={() => setTheme(v)} aria-pressed={t === v}>{compact && v === "system" ? "Auto" : l}</button>)}
    </div>
  );
};

/** The one bar across the top: the hoopoe and the name, where you are, the theme, outside-access status, the lock. */
export const TopBar: React.FC<{ due?: number }> = ({ due = 0 }) => {
  const [ext, setExt] = useState<{ enabled: boolean; running: boolean; url: string | null; error: string | null } | null>(null);
  useEffect(() => {
    let alive = true;
    const tick = () => api.external().then((s) => { if (alive) setExt(s); }).catch(() => undefined);
    tick();
    const t = setInterval(tick, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const queued = useQueueCount();
  const lock = async () => { try { await api.logout(); } catch { /* ignore */ } window.dispatchEvent(new Event("hudhud:locked")); };
  const cls = ({ isActive }: { isActive: boolean }) => (isActive ? "on" : "");
  return (
    <header className="topbar">
      <img src="/assets/brand/hudhud-icon.png" alt="" style={{ width: 28, height: 28, borderRadius: "50%" }} />
      <span className="name">Al-Hudhud<small>Reel Studio</small></span>
      <nav className="nav" aria-label="Main">
        <NavLink to="/" end className={cls}><Icon name="grid" size={15} /><span>Library</span></NavLink>
        <NavLink to="/posting" className={cls}><Icon name="send" size={15} /><span>Posting</span>{due ? <span className="badge">{due}</span> : null}</NavLink>
        <NavLink to="/settings" className={cls}><Icon name="settings" size={15} /><span>Settings</span></NavLink>
        <NavLink to="/queue" className={cls} title="Renders and translations: pause, cancel, run next"><Icon name="loop" size={15} /><span>Queue</span>{queued ? <span className="badge">{queued}</span> : null}</NavLink>
      </nav>
      <span className="spacer" />
      <SystemLoad />
      <ThemeSwitch />
      {ext?.enabled ? <span className="access" title={ext.url ?? ext.error ?? "starting the tunnel…"}><span className="dot" style={{ background: ext.url ? "var(--green)" : ext.error ? "var(--warn)" : "var(--gold)" }} />{ext.url ? `Outside access on · ${ext.url.replace("https://", "")}` : ext.error ? "Outside access: problem" : "Outside access starting…"}</span> : null}
      <button className="btn sm quiet" onClick={lock} title="Lock the studio (asks for the password again)">Lock</button>
    </header>
  );
};

/** On a phone the three destinations sit along the bottom, thumb-reach, like an app. */
export const TabBar: React.FC<{ due?: number }> = ({ due = 0 }) => {
  const cls = ({ isActive }: { isActive: boolean }) => (isActive ? "on" : "");
  const queued = useQueueCount();
  return (
    <nav className="tabbar" aria-label="Main">
      <NavLink to="/" end className={cls}><Icon name="grid" size={20} /><span>Library</span></NavLink>
      <NavLink to="/posting" className={cls}><Icon name="send" size={20} /><span>Posting</span>{due ? <span className="badge">{due}</span> : null}</NavLink>
      <NavLink to="/queue" className={cls}><Icon name="loop" size={20} /><span>Queue</span>{queued ? <span className="badge">{queued}</span> : null}</NavLink>
      <NavLink to="/settings" className={cls}><Icon name="settings" size={20} /><span>Settings</span></NavLink>
    </nav>
  );
};
